// The browser half of setup's Group and sort board (Livewire\Sites\Insights\SetupSort; D168 of
// documentation/pr-plans/insights-simplify-plan.md): its drag and drop, and its count.
//
// Drag and drop uses native drag events only: nothing on the page moves until the server answers, so Livewire's render
// stays the one source of what the board shows.
//
// - A search (a row of Not sorted yet) drops on a topic chip (moveHere: on the built-in Not relevant topic, set aside one
//   at a time), on a box (nameNewTopic: New topic in's dialog, which asks the new topic's name) or on Branded queries (chooseBrandTerm, which only opens the box's field: a brand
//   term catches every search with it, so it is never written by a drop). A ticked search carries every ticked search.
// - A search of the open topic's panel (D178) drops on another topic's chip or on a box, as above, naming its panel's
//   list, or on Not sorted yet (backToOther), when its topic holds it by name ([data-setup-sort-can-back]; every ticked
//   one, for a ticked row). Never on its own topic's chip, nor on Branded queries.
// - A topic chip drops on another topic's chip that takes one (askMerge, D180): the question asks before the merge is
//   written, since a topic dropped into another box can land on a chip there. Else it drops on another search intent's
//   box, or on a chip there that takes no topic, the built-in Not relevant topic's (setStage, as a drop on its box).
// - Unsafe queries' chip (D204, [data-setup-sort-chip-unsafe]) takes a search (moveToUnsafe) and a topic, which asks first
//   (askUnsafe), as its ⋯ items do; it is drawn with id 0 before the site has the topic, the first move making it.
//
// A finger drags by a grip ([data-setup-sort-handle]) through pointer events: touch browsers fire native drag events
// unevenly or not at all, so the grip (touch-action: none, so the list still scrolls under a finger anywhere else) starts
// the same drag, a label follows the finger, the target under it lights up as under a mouse, the page scrolls near its
// edges (and a box near its own), and lifting the finger lands it as a drop does (sd_land()). A mouse keeps the native drag.
//
// Every drop is a write the ⋯ menus also make, so the keyboard and a single pointer without dragging keep a way to each
// (WCAG 2.5.7). It sits on an element inside the board's root, whose Sort my searches half (insights-sort-searches.js)
// holds the ticks: ss_pickedIds() and ss_clearPick() are read through Alpine's scope.
//
// The count: a move asks for one (SetupSort::COUNT_EVENT); once the presses pause, SetupSortFigures reads the figures from
// BigQuery in a request of its own and says when it is done (SetupSortFigures::READY_EVENT); the board then reads them
// from the cache (recount()). A count read in the board's own request held every drop made meanwhile behind it. On Sort
// my queries the activity's half asks for that read (insights-sort-searches.js), the page's one read of the figures, so
// a move reads them once for both (P95, D211).
//
// At scale (P75, D196 of the plan; Nathan, 21 Oct: "If there were 80 topics in here, it's gonna be really tall"):
// - Find a topic dims every chip whose name does not hold what is typed, case and accents aside, says "n of m topics",
//   and scrolls each box to its first match; the status region says the count once the typing pauses.
// - Every box scrolls inside itself (boxes.blade.php's data-setup-sort-box-scroll; Ali, 22 Oct: no See more). An open
//   topic stands in place of the boxes (P82, D198; setup's board too since D202), so it never scrolls inside one.
// - A chip's press peeks at the topic's five biggest searches while a pointer rests on its name or the keyboard is on it:
//   SetupSortFigures reads every topic's in one statement (PEEK_EVENT, PEEKS_EVENT) as a peek first shows, and again
//   after a write made them stale; the peek describes the press while it shows, Escape dismisses it, a pointer may
//   move onto it (WCAG 1.4.13), and a scroll takes it along while its press is in sight.

import { coveredTop } from "./insights/covered-top.js";
import { send as sendWrite } from "./insights/writes.js";

// The list the tray's ticks are kept under (SetupSort::LIST).
const LIST = "other";

// The open topic's list, before its id (SetupSort::OPEN_LIST).
const OPEN_LIST = "topic:";

// Searches the AI was not sure of, on Sort my searches (SetupSort::UNSURE_LIST): they drag as Not sorted yet's do (P82).
const UNSURE_LIST = "unsure";

// The bulk bar's New topic in… menu's least width (min-w-[15rem]).
const BULK_MENU_WIDTH = 240;

// SetupSortFigures::READ_EVENT: the board asks for its figures to be read.
const READ_EVENT = "setup-sort-read";

// How long the presses must pause before the figures count again: a run of moves reads once.
const COUNT_PAUSE_MS = 1500;

// What the board sends as `from` with its writes' page events and counts (SetupSort::PAGE_SENDER).
const PAGE_SENDER = "sort-board";

// The board on Sort my queries (SetupSort::MODE_ACTIVITY), whose activity asks for the page's count.
const MODE_ACTIVITY = "activity";

// SetupSortFigures::PEEK_EVENT: the board asks for its chips' peeks.
const PEEK_EVENT = "setup-sort-peek";

// SetupSort::STATUS_EVENT: the board's polite status region.
const STATUS_EVENT = "sort-board-status";

// SetupSort::PEEK_ID: the board's one peek, which the press it shows for points at.
const PEEK_ID = "setup-sort-peek";

// How long a pointer rests on a chip's name before its peek shows, and how long a peek waits for a pointer to move onto it.
const PEEK_DELAY_MS = 350;
const PEEK_HIDE_MS = 150;

// The peek's gap from its press, and the least room it keeps from the window's sides.
const PEEK_GAP_PX = 6;
const PEEK_MARGIN_PX = 8;

// The room left above something a box scrolls into view inside itself (sd_scrollInside()).
const SCROLL_MARGIN_PX = 8;

// A finger drag scrolls the page this far per move while within EDGE_PX of the window's top or bottom, and a box this far
// while within BOX_EDGE_PX of the box's own (a box is short, and its chips sit close to its edges).
const EDGE_PX = 56;
const BOX_EDGE_PX = 24;
const EDGE_STEP_PX = 16;

// A name or a search folded for Find a topic: accents and case aside.
function folded(text) {
    return String(text ?? "")
        .normalize("NFD")
        .replace(/\p{M}/gu, "")
        .toLowerCase();
}

// The server's words with their placeholders filled (:count, :total, :query, :stage): the plural forms come unfilled, as
// the translator's selector chose them, so the browser counts.
function fill(text, values) {
    return String(text ?? "").replace(/:(count|total|query|stage)\b/g, (match, key) => (values[key] === undefined ? match : String(values[key])));
}

// A figure as the server prints one (number_format()).
function number(value) {
    return new Intl.NumberFormat("en-US").format(Number(value) || 0);
}

// A height a bar over the page writes on the document element, 0 while none is drawn: the Insights header's
// (insights-top-bar.js), and Sort my queries' thin bar's under it (insights-sort-tiles.js, P93).
function coverHeight(element, property) {
    return parseFloat(getComputedStyle(element).getPropertyValue(property)) || 0;
}

/**
 * @param {Object} words  the board's words (sort-board.blade.php's $boardWords): Find a topic's counts, the peek's and the
 *        search intents' names. Words only, so the board's x-data never changes between renders.
 */
export default function InsightsSetupSortBoard(words = {}) {
    // Where the pointer last moved. A pointer that enters or leaves a chip where it last moved has not moved: a box scrolled
    // under it, which must not change the peek (P75). Kept out of Alpine's reactive data, as every move writes it.
    let pointerAt = null;
    const notePointer = (event) => {
        pointerAt = { x: event.clientX, y: event.clientY };
    };
    const hasPointerMoved = (event) => pointerAt === null || event?.clientX !== pointerAt.x || event?.clientY !== pointerAt.y;

    return {
        words,

        sd_countTimer: null,

        // Who else asked for the count in flight (COUNT_EVENT's `from`, Business details on Sort my searches), kept until
        // its figures are read, so the board's recount() takes it as asked; null for the board's own moves.
        sd_countFrom: null,

        // Find a topic's text (P75).
        sd_find: "",

        // The renders of the board so far: what Find a topic counts reads it, so a render that changes the chips counts again.
        sd_renders: 0,

        // The board's own element and its listeners, kept from init(): a press inside a dialog has the dialog as $root.
        sd_rootElement: null,
        sd_unhook: null,
        sd_onScroll: null,

        // The peeks (P75): every topic's biggest searches by id, each [search, impressions], as SetupSortFigures last read
        // them, or null before; that read's state (idle, reading, ready, rate_limited, error); and whether a write since
        // made them stale, so the next peek reads them again while the old ones show.
        sd_peeks: null,
        sd_peekState: "idle",
        sd_peekStale: false,

        // The peek shown, {id, name, stage, searches}, its press, what showed it ("pointer" or "focus") and where it sits;
        // null while none shows.
        sd_peek: null,
        sd_peekTrigger: null,
        sd_peekBy: null,
        sd_peekAt: null,
        sd_peekTimer: null,
        sd_peekHideTimer: null,

        init() {
            this.sd_rootElement = this.$root;
            const livewire = typeof window !== "undefined" ? window.Livewire : null;
            const componentId = this.$wire?.$id;

            if (livewire && typeof livewire.hook === "function") {
                this.sd_unhook = livewire.hook("morphed", ({ component }) => {
                    if (component?.id === componentId) {
                        this.sd_renders++;
                    }
                });
            }

            // A peek sits by its press. A scroll, the page's or a box's, takes the keyboard's peek along while its press is in
            // sight, and away once it is not: a box scrolls a press the keyboard reached into view just after its peek
            // showed, and hiding it on every scroll left 7 of Top of funnel's 15 presses with no peek (P75). A pointer's
            // peek goes with any scroll, which moved its chip from under the pointer.
            this.sd_onScroll = () => {
                if (this.sd_peek === null) {
                    return;
                }

                if (this.sd_peekBy === "focus" && this.sd_isInSight(this.sd_peekTrigger)) {
                    this.sd_peekPlaceSoon();
                } else {
                    this.sd_peekHide();
                }
            };
            document.addEventListener("scroll", this.sd_onScroll, true);
            window.addEventListener("pointermove", notePointer, { passive: true });
        },

        destroy() {
            this.sd_unhook?.();
            document.removeEventListener("scroll", this.sd_onScroll, true);
            window.removeEventListener("pointermove", notePointer);
            clearTimeout(this.sd_peekTimer);
            clearTimeout(this.sd_peekHideTimer);
        },

        // What is being dragged: {kind: 'search', ids, from, topicId, canBack, inTopics} (from: null for Not sorted yet,
        // UNSURE_LIST for the searches the AI was not sure of, the topic each sits in being inTopics, else the open topic's
        // list, whose topic is topicId) or {kind: 'topic', id, stage}; null between drags.
        sd_drag: null,

        // The drop target under the pointer: 'box:<stage>', 'chip:<id>', 'branded' or 'tray'.
        sd_over: null,

        // A finger drag: its pointer's id, and the label that follows it ({x, y, text}); null otherwise.
        sd_touch: null,
        sd_ghost: null,

        // The element the page scrolls in, found on a finger drag's first edge.
        sd_scroller: null,

        // ---------------------------------------------------------------- starting a drag

        sd_startSearch(event, id, query, list = LIST) {
            // A finger already drags by the grip: the native drag a long press may start too would be a second one.
            if (event && this.sd_touch !== null) {
                event.preventDefault();

                return;
            }

            const picked = this.ss_pickedIds(list);
            const ids = picked.includes(id) ? picked : [id];
            const from = list === LIST ? null : list;
            const isOpen = from !== null && from !== UNSURE_LIST;

            this.sd_drag = {
                kind: "search",
                ids,
                from,
                topicId: isOpen ? Number(from.slice(OPEN_LIST.length)) : null,
                canBack: isOpen && ids.every((each) => this.sd_canBack(each)),
                inTopics: from === UNSURE_LIST ? ids.map((each) => this.sd_unsureTopic(each)) : [],
            };
            this.sd_begin(event, query);
        },

        // The topic a search of the AI not sure tab sits in, by its row's hook; NaN for a row not on the page.
        sd_unsureTopic(id) {
            return Number(this.$root.querySelector(`[data-sort-board-unsure-row="${id}"]`)?.dataset.sortBoardUnsureTopic);
        },

        // Whether the open topic's row takes Back to Not sorted yet: its topic holds it by name (SetupSort::backOf()).
        sd_canBack(id) {
            return this.$root.querySelector(`[data-setup-sort-search="${id}"][data-setup-sort-can-back]`) !== null;
        },

        sd_startTopic(event, id, stage, name) {
            if (event && this.sd_touch !== null) {
                event.preventDefault();

                return;
            }

            this.sd_drag = { kind: "topic", id: Number(id), stage };
            this.sd_begin(event, name);
        },

        sd_begin(event, text) {
            this.sd_peekHide();

            if (event?.dataTransfer) {
                event.dataTransfer.effectAllowed = "move";
                event.dataTransfer.setData("text/plain", String(text ?? ""));
            }
        },

        sd_end() {
            this.sd_drag = null;
            this.sd_over = null;
        },

        // ---------------------------------------------------------------- what is dragged, for the board's looks

        // Whether the bulk bar's New topic in… menu fits opening rightward from its button: on a wide tray the bar keeps the
        // button at its right end, where only a menu opening leftward stays on the tray.
        sd_opensRight(button) {
            const bar = button.closest("[data-setup-sort-bulk-bar]");

            return bar === null || button.getBoundingClientRect().left + BULK_MENU_WIDTH <= bar.getBoundingClientRect().right;
        },

        sd_isDragging(kind = null) {
            return this.sd_drag !== null && (kind === null || this.sd_drag.kind === kind);
        },

        sd_isDraggedSearch(id) {
            return this.sd_drag?.kind === "search" && this.sd_drag.ids.includes(id);
        },

        sd_isDraggedTopic(id) {
            return this.sd_drag?.kind === "topic" && this.sd_drag.id === Number(id);
        },

        // Whether $id is the chip of the topic the dragged searches are in: the one whose panel they come from, or, from the
        // AI not sure tab, the one every one of them sits in (P82), where a drop would move nothing.
        sd_isOwnChip(id) {
            const drag = this.sd_drag;

            if (drag?.kind !== "search" || drag.from === null) {
                return false;
            }

            return drag.from === UNSURE_LIST
                ? drag.inTopics.length > 0 && drag.inTopics.every((each) => each === Number(id))
                : Number(id) === drag.topicId;
        },

        // Whether the target would take what is dragged: a search of Not sorted yet goes anywhere but the tray it is in; one
        // of the open topic goes to another topic or a box, and to the tray when it can go back; one of the AI not sure tab
        // to a topic it is not in or a box; a topic goes onto another topic's chip that takes one, to merge into it (D180),
        // else to another search intent. $id is a chip's topic.
        sd_takes(target, stage = null, id = null) {
            const drag = this.sd_drag;

            if (drag === null) {
                return false;
            }

            if (drag.kind === "search") {
                return {
                    branded: drag.from === null,
                    tray: drag.from !== null && drag.canBack,
                    chip: !this.sd_isOwnChip(id),
                    box: true,
                }[target] ?? false;
            }

            // Unsafe queries takes any topic, after its question (D204), whatever box the topic sits in.
            if (target === "chip" && this.sd_chipUnsafe(id)) {
                return true;
            }

            if (target === "chip" && this.sd_chipMerges(id)) {
                return Number(id) !== drag.id;
            }

            return target !== "branded" && target !== "tray" && stage !== null && stage !== "" && stage !== drag.stage;
        },

        // Whether the chip of topic $id takes a dragged topic (D180), by its hook: one whose search intent may change. The
        // built-in Not relevant topic's chip and the AI's Unsafe searches' carry none.
        sd_chipMerges(id) {
            return id !== null && id !== undefined && this.$root.querySelector(`[data-setup-sort-topic="${Number(id)}"][data-setup-sort-chip-merges]`) !== null;
        },

        // Whether the chip of topic $id is Unsafe queries' for an editor (D204), by its hook: a search dropped there moves in,
        // and a topic asks first.
        sd_chipUnsafe(id) {
            return id !== null && id !== undefined && this.$root.querySelector(`[data-setup-sort-topic="${Number(id)}"][data-setup-sort-chip-unsafe]`) !== null;
        },

        sd_isOver(key) {
            return this.sd_over === key;
        },

        // ---------------------------------------------------------------- over and out

        sd_overTarget(event, key, target, stage = null, id = null) {
            if (!this.sd_takes(target, stage, id)) {
                // The open topic's own chip: no drop there, so its box under it lights up for none either (D178).
                if (target === "chip" && this.sd_isOwnChip(id)) {
                    event.stopPropagation();
                    this.sd_over = null;
                }

                return;
            }

            event.preventDefault();
            event.stopPropagation();

            if (event.dataTransfer) {
                event.dataTransfer.dropEffect = "move";
            }

            this.sd_over = key;
        },

        sd_leave(event, key) {
            if (event.currentTarget.contains(event.relatedTarget)) {
                return;
            }

            if (this.sd_over === key) {
                this.sd_over = null;
            }
        },

        // ---------------------------------------------------------------- drops

        sd_dropOnTopic(event, topicId, stage) {
            const drag = this.sd_take(event, "chip", stage, topicId);

            if (drag) {
                this.sd_land(drag, { target: "chip", id: Number(topicId), stage });
            }
        },

        // Not sorted yet takes a search of the open topic back (D178), but not under its chips or the open panel it holds
        // for a topic with no search intent: those are targets of their own, or no target.
        sd_overTray(event) {
            if (this.sd_isWithinTray(event.target)) {
                this.sd_overTarget(event, "tray", "tray");
            } else if (this.sd_over === "tray") {
                this.sd_over = null;
            }
        },

        sd_dropOnTray(event) {
            if (!this.sd_isWithinTray(event.target)) {
                return;
            }

            const drag = this.sd_take(event, "tray");

            if (drag) {
                this.sd_land(drag, { target: "tray" });
            }
        },

        sd_isWithinTray(element) {
            return element?.closest?.("[data-setup-sort-open], [data-setup-sort-topic]") === null;
        },

        sd_dropOnBox(event, stage) {
            const drag = this.sd_take(event, "box", stage);

            if (drag) {
                this.sd_land(drag, { target: "box", stage });
            }
        },

        sd_dropOnBranded(event) {
            const drag = this.sd_take(event, "branded");

            if (drag) {
                this.sd_land(drag, { target: "branded" });
            }
        },

        // What a drop does, a mouse's or a finger's: the ⋯ menus' own moves, naming the open topic's list for its rows.
        sd_land(drag, target) {
            if (target.target === "chip" && drag.kind === "search" && this.sd_chipUnsafe(target.id)) {
                // Unsafe queries' own move (D204), which makes the topic on a site that has none yet.
                this.sd_write("move", () => this.$wire.moveToUnsafe(drag.ids, drag.from));
            } else if (target.target === "chip" && drag.kind === "search") {
                this.sd_write("move", () => this.$wire.moveHere(target.id, drag.ids, drag.from));
            } else if (drag.kind === "topic" && target.target === "chip" && this.sd_chipUnsafe(target.id)) {
                // Asks before anything is written (D204), as the ⋯'s Unsafe queries does.
                this.$wire.askUnsafe(drag.id);
            } else if (target.target === "box" && drag.kind === "search") {
                // As the ⋯'s New topic in: its dialog asks the name before anything is written.
                this.$wire.nameNewTopic(target.stage, drag.ids, drag.from);
            } else if (target.target === "branded" && drag.kind === "search") {
                this.$wire.chooseBrandTerm(drag.ids);
            } else if (target.target === "tray" && drag.kind === "search") {
                this.sd_write("back", () => this.$wire.backToOther(drag.ids));
            } else if (drag.kind === "topic" && target.target === "chip" && this.sd_chipMerges(target.id)) {
                // Asks before anything is written (D180), as the ⋯'s Merge into… does: a topic dropped into another box
                // can land on a chip there.
                this.$wire.askMerge(drag.id, target.id);
            } else if (drag.kind === "topic" && target.target !== "branded" && target.target !== "tray") {
                this.sd_write("topic_stage", () => this.$wire.setStage(drag.id, target.stage));
            }
        },

        // The drag a drop ends, when the target takes it; the drag ends either way.
        sd_take(event, target, stage = null, id = null) {
            const drag = this.sd_drag;
            const takes = this.sd_takes(target, stage, id);

            if (takes) {
                event.preventDefault();
                event.stopPropagation();
            }

            this.sd_end();

            return takes ? drag : null;
        },

        // ---------------------------------------------------------------- a finger's drag

        sd_touchSearch(event, id, query, list = LIST) {
            if (this.sd_isFinger(event)) {
                this.sd_startSearch(null, id, query, list);
                this.sd_touchBegin(event, query);
            }
        },

        sd_touchTopic(event, id, stage, name) {
            if (this.sd_isFinger(event)) {
                this.sd_startTopic(null, id, stage, name);
                this.sd_touchBegin(event, name);
            }
        },

        // A mouse is left to the native drag the row or chip starts itself.
        sd_isFinger(event) {
            return event.pointerType === "touch" || event.pointerType === "pen";
        },

        sd_touchBegin(event, text) {
            event.preventDefault();
            event.currentTarget.setPointerCapture?.(event.pointerId);
            this.sd_touch = event.pointerId;
            this.sd_ghost = { x: event.clientX, y: event.clientY, text: String(text ?? "") };
        },

        sd_touchMove(event) {
            if (this.sd_touch !== event.pointerId) {
                return;
            }

            event.preventDefault();
            this.sd_ghost = { ...this.sd_ghost, x: event.clientX, y: event.clientY };
            this.sd_over = this.sd_targetAt(event.clientX, event.clientY)?.key ?? null;
            this.sd_edgeScroll(event.clientX, event.clientY);
        },

        sd_touchEnd(event) {
            if (this.sd_touch !== event.pointerId) {
                return;
            }

            const drag = this.sd_drag;
            const target = this.sd_targetAt(event.clientX, event.clientY);

            this.sd_touchStop();

            if (drag && target) {
                this.sd_land(drag, target);
            }
        },

        // Only a finger's own drag: a mouse's native drag cancels its pointer as it begins (Pointer Events), and ending
        // the drag then would drop the mouse's drag before it reached a target.
        sd_touchCancel(event) {
            if (this.sd_touch !== null && this.sd_touch === event.pointerId) {
                this.sd_touchStop();
            }
        },

        sd_touchStop() {
            this.sd_touch = null;
            this.sd_ghost = null;
            this.sd_end();
        },

        // The target under the finger that takes what is dragged, by the hooks the mouse's targets carry: a chip that
        // takes drops (data-setup-sort-chip-stage), the owner's Branded queries (data-setup-sort-takes-drops), Not sorted
        // yet outside its chips and open panel (data-setup-sort-tray), a box. A chip that would not take it hands over to
        // its box, as under a mouse.
        sd_targetAt(x, y) {
            const under = document.elementFromPoint(x, y);
            const chip = under?.closest("[data-setup-sort-chip-stage]");

            if (chip && this.sd_takes("chip", chip.dataset.setupSortChipStage, chip.dataset.setupSortTopic)) {
                return { target: "chip", key: `chip:${chip.dataset.setupSortTopic}`, id: Number(chip.dataset.setupSortTopic), stage: chip.dataset.setupSortChipStage };
            }

            // The chip of the topic the searches are in hands over to no box: a finger lifted there moves nothing, as a
            // mouse's drop.
            if (chip && this.sd_isOwnChip(chip.dataset.setupSortTopic)) {
                return null;
            }

            if (under?.closest("[data-setup-sort-branded][data-setup-sort-takes-drops]") && this.sd_takes("branded")) {
                return { target: "branded", key: "branded" };
            }

            if (under?.closest("[data-setup-sort-tray]") && this.sd_isWithinTray(under) && this.sd_takes("tray")) {
                return { target: "tray", key: "tray" };
            }

            const box = under?.closest("[data-setup-sort-box]");

            if (box && this.sd_takes("box", box.dataset.setupSortBox)) {
                return { target: "box", key: `box:${box.dataset.setupSortBox}`, stage: box.dataset.setupSortBox };
            }

            return null;
        },

        // A finger cannot reach what a mouse's drag scrolls to (under a mouse's drag near an edge, the browser scrolls a box,
        // or the page): near a box's own top or bottom, the box scrolls inside itself while it still can (P75); near the
        // window's, the page scrolls.
        sd_edgeScroll(x, y) {
            const box = document.elementFromPoint(x, y)?.closest("[data-setup-sort-box-scroll]");

            if (box) {
                const edges = box.getBoundingClientRect();

                if (y > edges.bottom - BOX_EDGE_PX && box.scrollTop + box.clientHeight < box.scrollHeight - 1) {
                    box.scrollBy(0, EDGE_STEP_PX);

                    return;
                }

                if (y < edges.top + BOX_EDGE_PX && box.scrollTop > 0) {
                    box.scrollBy(0, -EDGE_STEP_PX);

                    return;
                }
            }

            this.sd_scroller ??= this.sd_scrollerOf(this.$root);

            if (y > window.innerHeight - EDGE_PX) {
                this.sd_scroller.scrollBy(0, EDGE_STEP_PX);
            } else if (y < EDGE_PX + coverHeight(this.$root, "--insights-header-height") + coverHeight(this.$root, "--insights-sort-bar-height")) {
                this.sd_scroller.scrollBy(0, -EDGE_STEP_PX);
            }
        },

        sd_scrollerOf(element) {
            for (let node = element.parentElement; node && node !== document.body; node = node.parentElement) {
                if (/(auto|scroll)/.test(getComputedStyle(node).overflowY) && node.scrollHeight > node.clientHeight) {
                    return node;
                }
            }

            return document.scrollingElement;
        },

        // ---------------------------------------------------------------- the count

        // A move asked for a count (SetupSort::COUNT_EVENT): SetupSortFigures reads once the presses pause, asked here on
        // setup's board and by the activity's half on Sort my queries, whose summary counts from the same read (P95). The
        // move also changed what some topic holds, so the peeks are read again as the next one shows.
        sd_countSoon(detail) {
            if (detail && Number(detail.siteId) !== Number(this.$wire.siteId)) {
                return;
            }

            // The board's own count is sent as the board's for the activity; only another sender's is kept, since the
            // board's own moves set isCounting where it counts.
            if (typeof detail?.from === "string" && detail.from !== PAGE_SENDER) {
                this.sd_countFrom = detail.from;
            }

            this.sd_peekStale = true;

            if (this.$wire.mode === MODE_ACTIVITY) {
                return;
            }

            clearTimeout(this.sd_countTimer);
            this.sd_countTimer = setTimeout(() => window.Livewire?.dispatch(READ_EVENT, { siteId: Number(this.$wire.siteId) }), COUNT_PAUSE_MS);
        },

        // SetupSortFigures' read ended (SetupSortFigures::READY_EVENT): the board reads the figures it cached, or says why not.
        sd_figuresReady(detail) {
            if (!detail || Number(detail.siteId) !== Number(this.$wire.siteId)) {
                return;
            }

            if (detail.state === "ready") {
                const from = this.sd_countFrom;

                this.sd_countFrom = null;
                this.$wire.recount(from);
            } else {
                this.$wire.countFailed(detail.state);
            }
        },

        // Through the writes module, as every press of the board is; the ticks go once the write is in.
        async sd_write(action, livewire) {
            await sendWrite({ el: this.$root, action, payload: { by: "drop" }, livewire });
            this.ss_clearPick();
        },

        // ---------------------------------------------------------------- Find a topic (P75)

        sd_isFinding() {
            return folded(this.sd_find.trim()) !== "";
        },

        // Whether Find a topic leaves this chip out: its name does not hold what is typed.
        sd_isFindMiss(chip) {
            const text = folded(this.sd_find.trim());

            return text !== "" && !folded(chip?.dataset?.setupSortTopicName).includes(text);
        },

        // How many of the board's chips Find a topic finds, of all of them.
        sd_findCounts() {
            // A render that changed the chips counts again.
            void this.sd_renders;
            const text = folded(this.sd_find.trim());
            const names = [...(this.sd_rootElement?.querySelectorAll("[data-setup-sort-topic-name]") ?? [])].map((chip) => folded(chip.dataset.setupSortTopicName));

            return { found: names.filter((name) => name.includes(text)).length, total: names.length };
        },

        // "n of m topics", or no topic named so; under Find a topic while it is typed.
        sd_foundWords() {
            return this.sd_isFinding() ? this.sd_counted(this.words.found ?? [], this.sd_findCounts()) : "";
        },

        // Once the typing pauses, each box shows its first match: a box scrolls inside itself, so a match could sit below its
        // fold. Only the box scrolls, never the page.
        sd_revealFound() {
            const text = folded(this.sd_find.trim());

            if (text === "") {
                return;
            }

            this.sd_rootElement?.querySelectorAll("[data-setup-sort-box-scroll]").forEach((scroller) => {
                const match = [...scroller.querySelectorAll("[data-setup-sort-topic-name]")].find((chip) => folded(chip.dataset.setupSortTopicName).includes(text));

                if (match) {
                    this.sd_scrollInside(scroller, match);
                }
            });
        },

        // Scrolls $scroller so $element's top shows, when it does not already.
        sd_scrollInside(scroller, element) {
            const box = scroller.getBoundingClientRect();
            const at = element.getBoundingClientRect();

            if (at.top < box.top || at.top > box.bottom - 24) {
                scroller.scrollTop += at.top - box.top - SCROLL_MARGIN_PX;
            }
        },

        // The status region says the count once the typing pauses, since the field keeps focus while the chips dim.
        sd_sayFound() {
            if (this.sd_isFinding()) {
                window.dispatchEvent(new CustomEvent(STATUS_EVENT, { detail: { message: this.sd_counted(this.words.foundSaid ?? [], this.sd_findCounts()) } }));
            }
        },

        // Escape empties the field first; an empty field lets Escape through.
        sd_clearFind(event) {
            if (this.sd_find !== "") {
                event.preventDefault();
                event.stopPropagation();
                this.sd_find = "";
            }
        },

        // A plural set, [none, one, many], filled with what Find a topic counted.
        sd_counted(forms, { found, total }) {
            return fill(forms[Math.min(found, 2)] ?? "", { count: number(found), total: number(total), query: this.sd_find.trim() });
        },

        // ---------------------------------------------------------------- the peek (P75)

        // A pointer rests on a chip's name: its peek shows after a moment, at once when another peek already shows. A
        // finger's tap is the press, which opens the topic's searches, so it peeks at nothing.
        sd_peekSoon(event, press, id) {
            if (event?.pointerType === "touch" || this.sd_drag !== null || !hasPointerMoved(event)) {
                return;
            }

            clearTimeout(this.sd_peekHideTimer);
            clearTimeout(this.sd_peekTimer);

            if (this.sd_peek?.id === Number(id)) {
                return;
            }

            this.sd_peekTimer = setTimeout(() => this.sd_peekShow(press, id, "pointer"), this.sd_peek === null ? PEEK_DELAY_MS : 0);
        },

        // The keyboard reached a chip's press: its peek shows at once. A mouse's press focuses it too, with no ring; that one
        // peeks at nothing, since the press opens the searches themselves.
        sd_peekFocus(press, id) {
            if (press?.matches?.(":focus-visible")) {
                clearTimeout(this.sd_peekTimer);
                this.sd_peekShow(press, id, "focus");
            }
        },

        // A pointer left a chip's name, or the peek: the peek goes after a moment, unless the pointer never moved.
        sd_peekLeave(event) {
            if (!hasPointerMoved(event)) {
                return;
            }

            clearTimeout(this.sd_peekTimer);
            clearTimeout(this.sd_peekHideTimer);
            this.sd_peekHideTimer = setTimeout(() => this.sd_peekHide(), PEEK_HIDE_MS);
        },

        // A pointer moved onto the peek: it stays.
        sd_peekKeep() {
            clearTimeout(this.sd_peekHideTimer);
        },

        sd_peekHide() {
            clearTimeout(this.sd_peekTimer);
            clearTimeout(this.sd_peekHideTimer);
            this.sd_peek = null;
            this.sd_peekTrigger = null;
            this.sd_peekBy = null;
            this.sd_peekAt = null;
        },

        // Escape on the press dismisses its peek, and nothing else: a dialog round the board keeps its own Escape.
        sd_peekEscape(event) {
            if (this.sd_peek !== null) {
                event.stopPropagation();
                this.sd_peekHide();
            }
        },

        // A press on the peek is the chip's own press, Open all N.
        sd_peekPress() {
            const press = this.sd_peekTrigger;
            this.sd_peekHide();
            press?.click();
        },

        // The press whose peek shows is described by it.
        sd_peekDescribes(id) {
            return this.sd_peek?.id === Number(id) ? PEEK_ID : null;
        },

        sd_peekShow(press, id, by) {
            const chip = press?.closest?.("[data-setup-sort-topic]");

            if (!chip || !press.isConnected) {
                return;
            }

            const searches = chip.dataset.setupSortTopicSearches;
            this.sd_peekTrigger = press;
            this.sd_peekBy = by;
            this.sd_peekAt = null;
            this.sd_peek = {
                id: Number(id),
                name: chip.dataset.setupSortTopicName ?? "",
                stage: chip.dataset.setupSortTopicStage ?? "",
                searches: searches === undefined || searches === "" ? null : Number(searches),
            };
            this.sd_peeksAsk();
            this.sd_peekPlaceSoon();
        },

        // Once the peek shows (x-show shows a frame late), it sits under its press, or over it where the window has no room
        // below and it fits under what sticks over the page (coveredTop(), P89), held inside the window's sides.
        sd_peekPlaceSoon() {
            this.$nextTick(() => requestAnimationFrame(() => {
                const press = this.sd_peekTrigger;
                const panel = document.getElementById(PEEK_ID);

                if (this.sd_peek === null || !press?.isConnected || panel === null) {
                    return;
                }

                const box = press.getBoundingClientRect();
                const below = box.bottom + PEEK_GAP_PX;
                const above = box.top - PEEK_GAP_PX - panel.offsetHeight;
                const isAbove = below + panel.offsetHeight > window.innerHeight - PEEK_MARGIN_PX && above >= coveredTop() + PEEK_MARGIN_PX;

                this.sd_peekAt = {
                    top: isAbove ? above : below,
                    left: Math.max(PEEK_MARGIN_PX, Math.min(box.left, document.documentElement.clientWidth - panel.offsetWidth - PEEK_MARGIN_PX)),
                };
            }));
        },

        // Whether an element shows: inside the box that scrolls it, if one does, and inside the window under what sticks
        // over the page (coveredTop()).
        sd_isInSight(element) {
            if (!element?.isConnected) {
                return false;
            }

            const at = element.getBoundingClientRect();
            const box = element.closest("[data-setup-sort-box-scroll]")?.getBoundingClientRect();
            const top = Math.max(coveredTop(), box?.top ?? 0);
            const bottom = Math.min(window.innerHeight, box?.bottom ?? window.innerHeight);

            return at.bottom > top && at.top < bottom;
        },

        // The keyboard's focus inside a box kept clear of what sticks over the page (WCAG 2.4.11). A box's own scroll margin
        // keeps a focused chip inside the box's view (boxes.blade.php), so nothing kept it below the Insights header and the
        // boxes' head as the page scrolled to it; once the browser has scrolled the box, the page moves just enough.
        sd_keepFocusClear(event) {
            const element = event.target;

            if (!element?.closest?.("[data-setup-sort-box-scroll]")) {
                return;
            }

            requestAnimationFrame(() => {
                if (document.activeElement !== element) {
                    return;
                }

                const at = element.getBoundingClientRect();
                const clear = coveredTop() + SCROLL_MARGIN_PX;
                const floor = window.innerHeight - SCROLL_MARGIN_PX;
                const scroller = (this.sd_scroller ??= this.sd_scrollerOf(this.sd_rootElement ?? element));

                if (at.top < clear) {
                    scroller.scrollBy(0, at.top - clear);
                } else if (at.bottom > floor) {
                    scroller.scrollBy(0, at.bottom - floor);
                }
            });
        },

        sd_peekPlace() {
            if (this.sd_peekAt !== null) {
                return { top: `${this.sd_peekAt.top}px`, left: `${this.sd_peekAt.left}px` };
            }

            const box = this.sd_peekTrigger?.getBoundingClientRect?.();

            return box ? { top: `${box.bottom + PEEK_GAP_PX}px`, left: `${Math.max(PEEK_MARGIN_PX, box.left)}px` } : {};
        },

        // The peeks are read once a peek first shows, again after a read that failed, and again once a write made them
        // stale, the old ones showing meanwhile; never twice at once.
        sd_peeksAsk() {
            if (this.sd_peekState === "reading" || (this.sd_peeks !== null && !this.sd_peekStale)) {
                return;
            }

            this.sd_peekState = "reading";
            this.sd_peekStale = false;
            window.Livewire?.dispatch(PEEK_EVENT, { siteId: Number(this.$wire.siteId) });
        },

        // SetupSortFigures' peek read ended (SetupSortFigures::PEEKS_EVENT).
        sd_peeksRead(detail) {
            if (!detail || Number(detail.siteId) !== Number(this.$wire.siteId)) {
                return;
            }

            this.sd_peekState = detail.state;

            if (detail.state === "ready") {
                this.sd_peeks = detail.peeks ?? {};
            }

            this.sd_peekPlaceSoon();
        },

        sd_peekRows() {
            return this.sd_peek === null || this.sd_peeks === null ? [] : this.sd_peeks[this.sd_peek.id] ?? [];
        },

        // "34 queries · Top of funnel".
        sd_peekLine() {
            if (this.sd_peek === null) {
                return "";
            }

            const stages = this.words.stages ?? {};
            const stage = stages[this.sd_peek.stage] ?? stages.not_sorted ?? "";
            const count = this.sd_peek.searches;

            return count === null ? stage : fill((this.words.peekLine ?? [])[count === 1 ? 0 : 1], { count: number(count), stage });
        },

        // What shows in place of the searches: reading them, a topic holding none, or a read that failed.
        sd_peekNote() {
            if (this.sd_peek === null || this.sd_peekRows().length > 0) {
                return "";
            }

            if (this.sd_peekState === "reading" || (this.sd_peeks === null && this.sd_peekState === "idle")) {
                return this.words.peekReading ?? "";
            }

            if (this.sd_peeks !== null) {
                return this.words.peekEmpty ?? "";
            }

            return this.sd_peekState === "rate_limited" ? this.words.peekRateLimited ?? "" : this.words.peekFailed ?? "";
        },

        sd_peekOpenWords() {
            const count = this.sd_peek?.searches ?? this.sd_peekRows().length;

            return fill((this.words.peekOpenAll ?? [])[count === 1 ? 0 : 1], { count: number(count) });
        },

        sd_number(value) {
            return number(value);
        },
    };
}
